import { axiosInstance } from "@/plugins/axios";
import { moduleActionContext } from "@/store/index";
import { AxiosError, AxiosResponse } from "axios";
import { defineModule } from "direct-vuex";
import i18n from "../../assets/i18n";
import ListItemNumberModel from "../../models/listItemNumberModel";
import ListItemStringModel from "../../models/listItemStringModel";
import MailingContractorCarrierContactPersonRestModel from "../../models/rest/mailing/mailingContractorCarrierContactPersonRestModel";
import MailingContractorCarrierDataRestModel from "../../models/rest/mailing/mailingContractorCarrierDataRestModel";
import MailingContractorCarrierEmailDataModel from "../../models/rest/mailing/mailingContractorCarrierEmailDataModel";
import MailingContractorCarrierEmailPlaceDataModel from "../../models/rest/mailing/mailingContractorCarrierEmailPlaceDataModel";
import MailingContractorCarrierFormRestModel from "../../models/rest/mailing/mailingContractorCarrierFormRestModel";
import MailingContractorCarrierVehicleDetailRestModel from "../../models/rest/mailing/mailingContractorCarrierVehicleDetailRestModel";
import CountryRestModel from "../../models/rest/register/countryRestModel";


export interface MailingModuleStateInterface {
    allCountries: CountryRestModel[];
    mailingContractorCarrierFormData: MailingContractorCarrierFormRestModel;
    mailingVehicleTypes: ListItemStringModel[];
    loading: boolean;
    servedCountries: string[];
    showMailingContractorCarrierDialog: boolean;
    resolveMailingDialog: any;
    mailingContractorCarrierEmailData: MailingContractorCarrierEmailDataModel;
    mailingContractorCarrierFormErrorValidationError: any
}

const mailingModule = defineModule({
    namespaced: true,
    state: {
        allCountries: [] as CountryRestModel[],
        mailingContractorCarrierFormData: {} as MailingContractorCarrierFormRestModel,
        mailingVehicleTypes: [] as ListItemStringModel[],
        loading: false,
        servedCountries: [] as string[],
        showMailingContractorCarrierDialog: false,
        resolveMailingDialog: null,
        mailingContractorCarrierEmailData: {
            loading: { postalCode: "", city: "", country: "", time: "" } as MailingContractorCarrierEmailPlaceDataModel,
            unloading: { postalCode: "", city: "", country: "", time: "" } as MailingContractorCarrierEmailPlaceDataModel,
            count: "",
            unit: "",
            selectedVehicles: [] as ListItemNumberModel[],
            adr: false,
            lift: false,
        } as MailingContractorCarrierEmailDataModel,
        mailingContractorCarrierFormErrorValidationError: {} as any,
    } as MailingModuleStateInterface,
    mutations: {
        setAllCountries(state, payload: CountryRestModel[]) {
            state.allCountries = payload;
        },
        setMailingContractorCarrierFormData(state, payload: MailingContractorCarrierFormRestModel) {
            state.mailingContractorCarrierFormData = payload;
        },
        setMailingVehicleTypes(state, payload: ListItemStringModel[]) {
            state.mailingVehicleTypes = payload;
        },
        setServedCountries(state, payload: string[]) {
            state.servedCountries = payload;
        },
        setLoading(state, value: boolean) {
            state.loading = value;
        },
        setShowMailingContractorCarrierDialog(state, value: boolean) {
            state.showMailingContractorCarrierDialog = value;
        },
        setResolveMailingContractorCarrierDialog(state, value: any) {
            state.resolveMailingDialog = value;
        },
        setMailingContractorCarrierEmailData(state, value: MailingContractorCarrierEmailDataModel) {
            state.mailingContractorCarrierEmailData = value;
        },
        setMailingContractorCarrierFormErrorValidationError(state, value: any) {
            state.mailingContractorCarrierFormErrorValidationError= value;
        },
    },
    actions: {
        loadMailingDetails(context) {
            const { commit } = _moduleActionContext(context);
            return new Promise((resolve, reject) => {
                commit.setLoading(true);
                axiosInstance
                    .get("/api/mailing/details")
                    .then((resp: AxiosResponse<any>) => {
                        const data = resp.data;
                        const contractor = data.object.contractorCarrierData;
                        const contactPersons = data.object.contactPersons;
                        const vehicleDetails = data.object.vehicleDetails || [];
                        const servedCountries = data.object.servedCountries || [];
                        const mailingContractorCarrierFormData: MailingContractorCarrierFormRestModel = {
                            contractorCarrierData: contractor as MailingContractorCarrierDataRestModel,
                            vehicleDetails: vehicleDetails as MailingContractorCarrierVehicleDetailRestModel[],
                            contactPersons: contactPersons as MailingContractorCarrierContactPersonRestModel[],
                            servedCountries: servedCountries as string[],
                        };
                        commit.setMailingContractorCarrierFormData(mailingContractorCarrierFormData);
                        if (data.object.mailingVehicleTypes) {
                            commit.setMailingVehicleTypes(data.object.mailingVehicleTypes);
                        }
                        if (data.object.allCountries) {
                            commit.setAllCountries(data.object.allCountries);
                        }
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err))
                    .finally(() => {
                        commit.setLoading(false);
                    });
            });
        },
        saveMailingDetails(context) {
            const { state, commit, rootDispatch } = _moduleActionContext(context);
            return new Promise((resolve, reject) => {
                commit.setLoading(true);
                if (!state.mailingContractorCarrierFormData) {
                    commit.setLoading(false);
                    return reject("Brak danych formularza mailingFormData");
                }
                axiosInstance
                    .post("/api/mailing/details/save", {
                        contractorCarrierData: state.mailingContractorCarrierFormData.contractorCarrierData,
                        vehicleDetails: state.mailingContractorCarrierFormData.vehicleDetails,
                        contactPersons: state.mailingContractorCarrierFormData.contactPersons,
                        servedCountries: state.mailingContractorCarrierFormData.servedCountries,
                    })
                    .then((resp: AxiosResponse<any>) => {

                        commit.setMailingContractorCarrierFormErrorValidationError({});

                        if (resp.data.isSuccess == false) {
                            rootDispatch.confirm.openConfirmDialog({
                                showConfirmDialog: true,
                                title: i18n.t("common.errorWhileSaving").toString(),
                                message: resp.data.errorMessage,
                                links: [],
                                options: { buttonColor: "error", layoutColor: "error" },
                                buttonType: "ok"
                            });
                        }
                        else {
                            rootDispatch.confirm.openConfirmDialog({
                                showConfirmDialog: true,
                                title: i18n.t("common.savedSuccessfully").toString(),
                                message: i18n.t("common.savedSuccessfully").toString(),
                                links: [],
                                options: { buttonColor: "primary", layoutColor: "primary" },
                                buttonType: "ok"
                            });
                            resolve(resp.data);
                        }
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => {
                        if (err.response && err.response.data.errors) {
                            commit.setMailingContractorCarrierFormErrorValidationError(err.response.data.errors);
                            resolve(err.response.data.errors);
                        }
                        reject(err)
                    })
                    .finally(() => {
                        commit.setLoading(false);
                    });
            });
        },
        isConfirmedMailingContractorCarrierDialog(context, payload: { isConfirmed: boolean }) {
            const { state, commit } = _moduleActionContext(context);
            state.resolveMailingDialog(true);
            commit.setShowMailingContractorCarrierDialog(false);
        },

        isNotConfirmedMailingContractorCarrierDialog(context) {
            const { state, commit } = _moduleActionContext(context);
            state.resolveMailingDialog(false);
            commit.setShowMailingContractorCarrierDialog(false);
        },
        openMailingContractorCarrierDialog(
            context,
            payload: {
                showMailingDialog: boolean;
            }
        ) {
            const { commit } = _moduleActionContext(context);
            commit.setShowMailingContractorCarrierDialog(payload.showMailingDialog);
            return new Promise((resolve, reject) => {
                commit.setResolveMailingContractorCarrierDialog(resolve);
            });
        },
        async sendMailingContractorCarrierEmail(context) {
            const { state, commit, rootDispatch } = _moduleActionContext(context);
            commit.setLoading(true);
            try {
                const resp: AxiosResponse<any> = await axiosInstance
                    .post("/api/mailing/send-carrier-carrier-mailing-emails", state.mailingContractorCarrierEmailData);
                if (resp.data.isSuccess === false) {
                    rootDispatch.confirm.openConfirmDialog({
                        showConfirmDialog: true,
                        title: i18n.t("common.errorWhileSending").toString(),
                        message: resp.data.errorMessage,
                        links: [],
                        options: { buttonColor: "error", layoutColor: "error" },
                        buttonType: "ok",
                    });
                }
                return resp.data;
            } catch (err) {
                rootDispatch.confirm.openConfirmDialog({
                    showConfirmDialog: true,
                    title: i18n.t("common.errorWhileSending").toString(),
                    message: (err as AxiosError).message,
                    links: [],
                    options: { buttonColor: "error", layoutColor: "error" },
                    buttonType: "ok",
                });
                return Promise.reject(err);
            } finally {
                commit.setLoading(false);
            }
        },
    }
});

export default mailingModule;
const _moduleActionContext = (context: any) => moduleActionContext(context, mailingModule);
